// ============================================================================
// APPLICATION CONSTANTS
// ============================================================================

export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const TOKEN_KEY = 'mis_access_token';
export const REFRESH_TOKEN_KEY = 'mis_refresh_token';
export const USER_KEY = 'mis_user';

// Trade Types
export const TRADE_TYPES = [
  { value: 'Equity', label: 'Equity' },
  { value: 'Future', label: 'Future' },
  { value: 'Option', label: 'Option' },
];

// Trade Status
export const TRADE_STATUS = [
  { value: '', label: 'All' },
  { value: 'OPEN', label: 'Open' },
  { value: 'CLOSED', label: 'Closed' },
];

// User Roles
export const USER_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'agent', label: 'Agent' },
  { value: 'client', label: 'Client' },
];

// Risk Thresholds
export const RISK_THRESHOLDS = {
  PNL_WARNING: -10,
  PNL_CRITICAL: -20,
  RABBIT_WARNING: 1.0,
  RABBIT_CRITICAL: 0.5,
};

// Premium Theme Colors
export const COLORS = {
  profit: '#059669',
  loss: '#DC2626',
  neutral: '#D97706',
  primary: '#EC4899',
  secondary: '#8B5CF6',
  warning: '#D97706',
  info: '#0284C7',
  success: '#059669',
  error: '#DC2626',
};

// Chart Colors
export const CHART_COLORS = {
  positive: '#059669',
  negative: '#DC2626',
  range: '#D97706',
  primary: '#EC4899',
  secondary: '#8B5CF6',
};

// Date Formats
export const DATE_FORMAT = 'yyyy-MM-dd';
export const DATE_DISPLAY_FORMAT = 'dd MMM yyyy';
export const DATETIME_FORMAT = 'yyyy-MM-dd HH:mm:ss';

// Pagination
export const DEFAULT_PAGE_SIZE = 50;
export const PAGE_SIZE_OPTIONS = [25, 50, 100];

// ============================================================================
// STOCK SYMBOLS - Organized by Sector
// ============================================================================

// Indices & Exchanges (4 symbols)
export const INDEX_SYMBOLS = [
  'BANKNIFTY', 'NIFTY', 'MCX', 'BSE',
];

// Banking & Finance (28 symbols)
export const BANKING_FINANCE_SYMBOLS = [
  'HDFCBANK', 'ICICIBANK', 'KOTAKBANK', 'AXISBANK', 'INDUSINDBK', 'SBIN', 'AUBANK',
  'BAJFINANCE', 'BAJAJFINSV', 'CHOLAFIN', 'MUTHOOTFIN', 'SBILIFE', 'ICICIGI',
  'CANBK', 'UNIONBANK', 'PNB', 'INDIANB', 'BANKINDIA', 'BANKBARODA', 'MAHABANK', 'FEDERALBNK',
  'SHRIRAMFIN', 'ABCAPITAL', 'CHOLAHLDNG', 'MFSL', 'MOTILALOFS', 'NUVAMA', 'TATACAP',
];

// IT & Tech (10 symbols)
export const IT_TECH_SYMBOLS = [
  'TCS', 'INFY', 'HCLTECH', 'TECHM', 'LTIM', 'PERSISTENT', 'COFORGE',
  'TATAELXSI', 'TATATECH', 'CDSL',
];

// Auto & Ancillary (14 symbols)
export const AUTO_SYMBOLS = [
  'MARUTI', 'BAJAJ_AUTO', 'EICHERMOT', 'M&M', 'TVSMOTOR', 'HEROMOTOCO', 'ASHOKLEY',
  'ESCORTS', 'ESCONET', 'BOSCHLTD', 'EXIDEIND', 'TIMKEN', 'TMCV', 'MRF',
];

// Pharma & Healthcare (14 symbols)
export const PHARMA_HEALTHCARE_SYMBOLS = [
  'SUNPHARMA', 'DRREDDY', 'DIVISLAB', 'CIPLA', 'LUPIN', 'AUROPHARMA', 'ALKEM',
  'LAURUSLABS', 'IPCALAB', 'SYNGENE', 'ASTERDM', 'MAXHEALTH', 'APOLLO', 'GLENMARK',
];

// Consumer & FMCG (12 symbols)
export const CONSUMER_FMCG_SYMBOLS = [
  'HINDUNILVR', 'ITC', 'BRITANNIA', 'NESTLEIND', 'GODREJCP', 'COLPAL',
  'TATACONSUM', 'ASIANPAINT', 'PIDILITIND', 'TITAN', 'JUBLFOOD', 'SWIGGY',
];

// Metals & Mining (6 symbols)
export const METALS_MINING_SYMBOLS = [
  'TATASTEEL', 'JSWSTEEL', 'HINDZINC', 'HINDCOPPER', 'NMDC', 'HINDALCO',
];

// Energy & Power (6 symbols)
export const ENERGY_POWER_SYMBOLS = [
  'RELIANCE', 'NTPC', 'TATAPOWER', 'SJVN', 'INDUSTOWER', 'ADANIPOWER',
];

// Cement & Construction (9 symbols)
export const CEMENT_CONSTRUCTION_SYMBOLS = [
  'ULTRACEMCO', 'SHREECEM', 'JSWCEMENT',
  'LT', 'DLF', 'NBCC', 'RVNL', 'GMRAIRPORT', 'ADANIPORTS',
];

// Industrials & Capital Goods (9 symbols)
export const INDUSTRIALS_SYMBOLS = [
  'BEL', 'HAL', 'BHEL', 'ABB', 'AIAENG', 'BDL', 'MAZDOCK', 'PARAS', 'PIIND',
];

// Telecom & Digital Services (5 symbols)
export const TELECOM_DIGITAL_SYMBOLS = [
  'BHARTIARTL', 'TATACOMM', 'NYKAA', 'IRCTC', 'DELHIVERY',
];

// Chemicals, Materials & Agriculture (9 symbols)
export const CHEMICALS_AGRI_SYMBOLS = [
  'SRF', 'TATACHEM', 'NAVINFLUOR', 'RCF', 'CHAMBLFERT',
  'CIANAGRO', 'KRISHNADEF', 'PREMEXPLN', 'FACT',
];

// Diversified & Specialty (10 symbols)
export const DIVERSIFIED_SYMBOLS = [
  'LTF', 'JIOFIN', 'POLYCAB', 'SOLARINDS', 'SUPREMEIND', 'CGPOWER', 'CUMMINSIND',
  'PAGEIND', 'INDHOTEL', 'WAAREEENER',
];

// Emerging & Small Cap (16 symbols)
export const EMERGING_SMALLCAP_SYMBOLS = [
  'ETERNAL', 'TRENT', 'KALYANKJIL', 'OFSS', 'CAMS',
  'SWANCORP', 'BBOX', 'AZAD', 'HEXT', 'APLAPOLLO',
  'GODREJPROP', 'GODREJIND', 'GODREJAGRO', 'JSWENERGY', 'JSWINFRA', 'ABFRL',
];

// All symbols combined for autocomplete - organized by sector
export const ALL_SYMBOLS = [
  // Indices & Exchanges
  ...INDEX_SYMBOLS,
  // Banking & Finance
  ...BANKING_FINANCE_SYMBOLS,
  // IT & Tech
  ...IT_TECH_SYMBOLS,
  // Auto & Ancillary
  ...AUTO_SYMBOLS,
  // Pharma & Healthcare
  ...PHARMA_HEALTHCARE_SYMBOLS,
  // Consumer & FMCG
  ...CONSUMER_FMCG_SYMBOLS,
  // Metals & Mining
  ...METALS_MINING_SYMBOLS,
  // Energy & Power
  ...ENERGY_POWER_SYMBOLS,
  // Cement & Construction
  ...CEMENT_CONSTRUCTION_SYMBOLS,
  // Industrials & Capital Goods
  ...INDUSTRIALS_SYMBOLS,
  // Telecom & Digital Services
  ...TELECOM_DIGITAL_SYMBOLS,
  // Chemicals, Materials & Agriculture
  ...CHEMICALS_AGRI_SYMBOLS,
  // Diversified & Specialty
  ...DIVERSIFIED_SYMBOLS,
  // Emerging & Small Cap
  ...EMERGING_SMALLCAP_SYMBOLS,
];

// Symbol Categories for filtering
export const SYMBOL_CATEGORIES = [
  { value: 'all', label: 'All Symbols', symbols: ALL_SYMBOLS },
  { value: 'indices', label: 'Indices & Exchanges', symbols: INDEX_SYMBOLS },
  { value: 'banking', label: 'Banking & Finance', symbols: BANKING_FINANCE_SYMBOLS },
  { value: 'it', label: 'IT & Tech', symbols: IT_TECH_SYMBOLS },
  { value: 'auto', label: 'Auto & Ancillary', symbols: AUTO_SYMBOLS },
  { value: 'pharma', label: 'Pharma & Healthcare', symbols: PHARMA_HEALTHCARE_SYMBOLS },
  { value: 'fmcg', label: 'Consumer & FMCG', symbols: CONSUMER_FMCG_SYMBOLS },
  { value: 'metals', label: 'Metals & Mining', symbols: METALS_MINING_SYMBOLS },
  { value: 'energy', label: 'Energy & Power', symbols: ENERGY_POWER_SYMBOLS },
  { value: 'cement', label: 'Cement & Construction', symbols: CEMENT_CONSTRUCTION_SYMBOLS },
  { value: 'industrials', label: 'Industrials & Capital Goods', symbols: INDUSTRIALS_SYMBOLS },
  { value: 'telecom', label: 'Telecom & Digital', symbols: TELECOM_DIGITAL_SYMBOLS },
  { value: 'chemicals', label: 'Chemicals & Agriculture', symbols: CHEMICALS_AGRI_SYMBOLS },
  { value: 'diversified', label: 'Diversified & Specialty', symbols: DIVERSIFIED_SYMBOLS },
  { value: 'smallcap', label: 'Emerging & Small Cap', symbols: EMERGING_SMALLCAP_SYMBOLS },
];

// Legacy export for backward compatibility
export const COMMON_SCRIPS = ALL_SYMBOLS;

// Lot Sizes - Comprehensive NSE F&O lot sizes
export const LOT_SIZE_MAP: Record<string, number> = {
  // Indices
  NIFTY: 75, BANKNIFTY: 15, FINNIFTY: 25, MIDCPNIFTY: 50,
  // Banking & Finance
  HDFCBANK: 550, ICICIBANK: 700, KOTAKBANK: 400, AXISBANK: 900, INDUSINDBK: 500,
  SBIN: 1500, AUBANK: 1000, BAJFINANCE: 125, BAJAJFINSV: 125, CHOLAFIN: 500,
  MUTHOOTFIN: 500, SBILIFE: 750, ICICIGI: 250, CANBK: 6300, PNB: 6000,
  BANKBARODA: 2925, FEDERALBNK: 5000, SHRIRAMFIN: 250, ABCAPITAL: 2500,
  MFSL: 500, MOTILALOFS: 800,
  // IT & Tech
  TCS: 175, INFY: 300, HCLTECH: 350, TECHM: 600, LTIM: 150, PERSISTENT: 150,
  COFORGE: 100, TATAELXSI: 100, CDSL: 500,
  // Auto
  MARUTI: 100, BAJAJ_AUTO: 125, EICHERMOT: 150, 'M&M': 350, TVSMOTOR: 350,
  HEROMOTOCO: 150, ASHOKLEY: 4500, ESCORTS: 275, BOSCHLTD: 25, EXIDEIND: 1800, MRF: 5,
  // Pharma
  SUNPHARMA: 700, DRREDDY: 125, DIVISLAB: 200, CIPLA: 650, LUPIN: 550,
  AUROPHARMA: 500, ALKEM: 150, LAURUSLABS: 1700, IPCALAB: 650, APOLLO: 125, GLENMARK: 700,
  // Consumer & FMCG
  HINDUNILVR: 300, ITC: 1600, BRITANNIA: 200, NESTLEIND: 200, GODREJCP: 500,
  COLPAL: 325, TATACONSUM: 900, ASIANPAINT: 300, PIDILITIND: 250, TITAN: 375,
  JUBLFOOD: 1250,
  // Metals
  TATASTEEL: 5500, JSWSTEEL: 675, HINDZINC: 2100, HINDCOPPER: 3200, NMDC: 3350, HINDALCO: 1075,
  // Energy
  RELIANCE: 250, NTPC: 2700, TATAPOWER: 2700, ADANIPOWER: 1600,
  // Cement & Construction
  ULTRACEMCO: 100, SHREECEM: 25, LT: 375, DLF: 825, ADANIPORTS: 625,
  // Industrials
  BEL: 3350, HAL: 150, BHEL: 4500, ABB: 125, BDL: 400, PIIND: 175,
  // Telecom
  BHARTIARTL: 457, IRCTC: 575,
  // Chemicals
  SRF: 125, TATACHEM: 500, NAVINFLUOR: 150,
  // Diversified
  POLYCAB: 125, CUMMINSIND: 200, PAGEIND: 15, INDHOTEL: 1150,
  // Emerging
  TRENT: 200,
};

// Legacy export
export const COMMON_LOT_SIZES = Object.entries(LOT_SIZE_MAP).map(([scrip, lotSize]) => ({ scrip, lotSize }));

// Alert Messages
export const ALERT_MESSAGES = {
  LOGIN_SUCCESS: 'Login successful',
  LOGIN_FAILED: 'Invalid email or password',
  LOGOUT_SUCCESS: 'Logged out successfully',
  TRADE_CREATED: 'Trade created successfully',
  TRADE_UPDATED: 'Trade updated successfully',
  TRADE_DELETED: 'Trade deleted successfully',
  CLIENT_CREATED: 'Client created successfully',
  CLIENT_UPDATED: 'Client updated successfully',
  PAYOUT_CREATED: 'Payout created successfully',
  PAYOUT_UPDATED: 'Payout updated successfully',
  PAYOUT_DELETED: 'Payout deleted successfully',
  GENERIC_ERROR: 'An error occurred. Please try again.',
  SESSION_EXPIRED: 'Your session has expired. Please login again.',
};

// Routes
export const ROUTES = {
  LOGIN: '/login',
  DASHBOARD: '/',
  TRADES: '/trades',
  TRADE_ENTRY: '/trades/new',
  TRADE_EDIT: '/trades/:id/edit',
  CLIENTS: '/clients',
  CLIENT_DETAIL: '/clients/:id',
  RISK_DASHBOARD: '/risk',
  PAYOUTS: '/payouts',
  ANALYTICS: '/analytics',
  MARKET: '/market',
  SETTINGS: '/settings',
  PROFILE: '/profile',
  // HR/Payroll Module
  EMPLOYEES: '/employees',
  ATTENDANCE: '/attendance',
  SALARY_SLIPS: '/salary-slips',
};

// ============================================================================
// INDIAN REGULATORY CHARGE RATES
// ============================================================================

export const CHARGE_RATES = {
  // STT
  STT_EQUITY_DELIVERY_BUY: 0.001,
  STT_EQUITY_DELIVERY_SELL: 0.001,
  STT_EQUITY_INTRADAY_SELL: 0.00025,
  STT_FUTURE_SELL: 0.0005,
  STT_OPTION_BUY: 0,            // NIL — STT on options is sell-side only
  STT_OPTION_SELL: 0.0015,      // 0.15% sell side only (Bajaj convention)
  // Exchange Transaction Charges
  EXCHANGE_EQUITY_NSE: 0.0000297,
  EXCHANGE_EQUITY_BSE: 0.0000375,
  EXCHANGE_FUTURE_NSE: 0.0000173,
  EXCHANGE_FUTURE_BSE: 0,            // NIL for BSE Futures
  EXCHANGE_OPTION: 0.0003503,
  // SEBI
  SEBI_RATE: 0.000001,
  // Stamp Duty (BUY side only)
  STAMP_EQUITY_DELIVERY: 0.00015,   // 0.015% per Indian Stamp Act (eff 1 Jul 2020)
  STAMP_EQUITY_INTRADAY: 0.00003,
  STAMP_FUTURE: 0.00002,
  STAMP_OPTION: 0.00003,
  // GST
  GST_RATE: 0.18,
  // Default brokerage rates
  DEFAULT_FUTURE_BROKERAGE: 0.0003,
  DEFAULT_OPTION_BROKERAGE: 0.0015,
};
